import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import get from "lodash.get";

import { saveSpecSubmittal } from "../../../../../actions/submittalActions";
import {
  showSnackNotificationAction,
  closeModalDialog,
} from "../../../../../actions/layoutActions";
import useActions from "../../../../hooks/useActions";

import { Wrapper, DASHBOARD_HEIGHT } from "../ReviewSubmittals/components";
import { dataComponentId } from "../../../../../actions/specDetailActions";
import Grid from "../ReviewSubmittals/Grid";
import SubmittalDescriptions, {
  hasDescriptionChanges,
} from "../ReviewSubmittals/SubmittalDescriptions";
import ReviewActions from "../ReviewSubmittals/ReviewActions";
import { useSubmittalSave } from "../ReviewSubmittals/useSubmittalSave";
import {
  uploadFileActions,
  updateSpecsByRequirement,
} from "./uploadFileActions";
import propTypes from "../../../../../constants/propTypes";
import { DragDropDashboard } from "../../../../inputs/DragDrop/DragDropDashboard";
import { LoaderContext } from "../../../../ui/Loader";
import { useV2DatacomponentResources } from "../../../../hooks/useResources";
import * as REQUEST_TYPES from "../../../../../constants/RequestTypes";

const applyDescriptions = (specRequirement, descriptions, override) => {
  Object.entries(descriptions).forEach(([field, value]) => {
    if (!value?.trim()) return;
    if (override || !specRequirement[field]) {
      specRequirement[field] = value;
    }
  });
};

const UploadFiles = ({
  selectedSpecRequirementIds,
  onUpdateSpec,
  afterSave,
  onBack,
  ...props
}) => {
  const [submittalFiles, setSubmittalFiles] = useState([]);
  const [loaderActive, setLoaderActive] = useState();
  const newSubmittalFilesRef = useRef([]);
  const uploadCount = useRef(0);
  const [receivedDescription, setReceivedDescription] = useState("");
  const [approvedDescription, setApprovedDescription] = useState("");
  const [overrideContent, setOverrideContent] = useState(false);
  const { isSaving, save } = useSubmittalSave(afterSave);

  const actions = useActions({
    saveSpecSubmittal,
    showSnackNotificationAction,
    closeModalDialog,
  });

  const spec = useV2DatacomponentResources(
    dataComponentId,
    null,
    REQUEST_TYPES.FIND
  );
  const specRequirements = get(
    props,
    "specRequirements",
    get(spec, "specRequirements")
  );

  const selectedSpecRequirements = specRequirements.filter(specRequirement =>
    selectedSpecRequirementIds.includes(specRequirement.id)
  );

  const handleUpdate = () => {
    selectedSpecRequirements.forEach(sr => {
      applyDescriptions(
        sr,
        { receivedDescription, approvedDescription },
        overrideContent
      );
    });
    return save(() => updateSpecsByRequirement(onUpdateSpec, specRequirements));
  };

  const hasChanges = hasDescriptionChanges({
    receivedDescription,
    approvedDescription,
  });

  // Uploading, approving or deleting a file keeps the panel open, so the new
  // file can be approved right away; only Update or Back leave it
  const handleClear = () => setLoaderActive(null);

  const { handleUpload, handleApprove, handleDelete } = uploadFileActions({
    selectedSpecRequirements,
    actions,
    onUpdateSpec,
    spec,
    specRequirements,
    newSubmittalFilesRef,
    uploadCount,
    setSubmittalFiles,
    submittalFiles,
    setLoaderActive,
    handleClear,
  });

  const allSubmittalFiles = selectedSpecRequirements.reduce(
    (arr, { submittalFiles = [] }) => [...arr, ...submittalFiles],
    []
  );

  const referenceSubmittalFiles = submittalFiles.map(
    ({ file: { s3Key }, uploadCount }) => ({
      ...allSubmittalFiles.find(
        submittalFile => s3Key == submittalFile.file.s3Key
      ),
      uploadCount,
    })
  );
  const referenceSpecRequirement = specRequirements.find(
    specRequirement =>
      specRequirement.id == referenceSubmittalFiles.at(0)?.specRequirementId
  );

  // Every upload toggles the page's loading once per selected requirement; if
  // it reached the description inputs they would flip to skeletons and back,
  // resizing the panel and flashing its scrollbar while the upload runs
  return (
    <LoaderContext.Provider value={{ loading: false }}>
      <Wrapper>
        <SubmittalDescriptions
          receivedDescription={receivedDescription}
          approvedDescription={approvedDescription}
          onReceivedDescriptionChange={setReceivedDescription}
          onApprovedDescriptionChange={setApprovedDescription}
          showOverride={selectedSpecRequirements.length > 1}
          overrideContent={overrideContent}
          onOverrideContentChange={setOverrideContent}
        />
        <LoaderContext.Provider value={{ loading: loaderActive }}>
          <Grid
            gridId={"bulk-submittal-files"}
            specRequirement={{
              ...referenceSpecRequirement,
              submittalFiles: referenceSubmittalFiles,
            }}
            onDelete={handleDelete}
            onApprove={(submittal, row) => handleApprove(row)(submittal)}
            loaderActive={loaderActive}
          />
        </LoaderContext.Provider>
        <DragDropDashboard onChange={handleUpload} height={DASHBOARD_HEIGHT} />
        <ReviewActions
          isBusy={!!loaderActive || isSaving}
          isSaving={isSaving}
          hasChanges={hasChanges}
          onUpdate={handleUpdate}
          // In the Spec Detail modal there's no panel to go back to, so Close
          // closes the modal; file actions save right away and don't need Update
          onBack={onBack || actions.closeModalDialog}
        />
      </Wrapper>
    </LoaderContext.Provider>
  );
};

UploadFiles.propTypes = {
  spec: propTypes.spec,
  selectedSpecRequirementIds: PropTypes.array.isRequired,
  onUpdateSpec: PropTypes.func.isRequired,
  specRequirements: PropTypes.array,
  afterSave: PropTypes.func,
  onBack: PropTypes.func,
};

export default UploadFiles;
